import React, { useEffect } from "react";
import { CheckCircledIcon, LinkBreak2Icon } from "@radix-ui/react-icons";
import { Icon } from '@src/components/icon/Icon';
import { CustomTooltip } from '../tooltip/Tooltip';
import cn from 'classnames';
import { ReactflowInstanceContext } from '@src/scenario-editor/ScenarioEditor';
import { reactFlowInstanceAtom } from '@src/atoms';
import { useContext } from 'react';
import type { CustomGenericNodeData } from '@src/components/custom-nodes/types';
import { ALLOWED_GOTO_TYPES } from '@src/consts';

import { Input } from "@/components/ui/input";

import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@/components/ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

export interface Option {
  value: string
  label: string
}

export interface ValueRendererProps {
  children?: React.ReactNode
}
const DefaultValueRenderer = ({ children }: ValueRendererProps) => (
  <div className="w-full flex flex-wrap items-center">
    {children}
  </div>
);

export interface ComboboxGotoPickerProps {
  value?: Option
  withCustomOptions?: boolean
  disabled?: boolean
  onValueChange?: (value: Option, extraOpts?: Record<string, unknown>) => void,
  Renderer?: (props: ValueRendererProps) => React.ReactNode
}
export function ComboboxGotoPicker({
    value,
    withCustomOptions = false,
    onValueChange,
    Renderer=DefaultValueRenderer,
    disabled = false
  }: ComboboxGotoPickerProps) {
  const [open, setOpen] = React.useState(false)
  const [search, setSearch] = React.useState("");
  const searchRef = React.useRef<HTMLInputElement>(null);
  const [options, setOptions] = React.useState<Option[]>([]);
  const reactFlowInstance = useContext(ReactflowInstanceContext);
  const instance = reactFlowInstance.get(reactFlowInstanceAtom)!;
  const nodes = instance.getNodes();



  useEffect(() => {
    const filteredNodes = nodes.filter((node) => ALLOWED_GOTO_TYPES.includes((node.data as CustomGenericNodeData)?.nodeType ?? ''));
    setOptions(filteredNodes.map((node) => ({
      value: node.id,
      label: (node?.data as CustomGenericNodeData)?.message ?? (node?.data as CustomGenericNodeData)?.title ?? (node?.data as CustomGenericNodeData)?.label,
    })));
  }, [nodes.length]);

  const canAddOption =
    search.length > 0
    && !(options ?? []).some((opt) => opt.label.toLowerCase() === search.toLowerCase())
    && withCustomOptions;

  const searchChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  }

  const selectedOptionLabel = value?.label;

  const isValueValid =
    !value?.value
    || !options.length
    || options?.find((opt) => opt.value === value?.value);
  const tooltipItemMessage = isValueValid ? ((value?.value && value?.value !== '') ? value?.value : 'Select target') : 'The associated object was deleted';
  return (
    <div>
      <Popover open={open} onOpenChange={setOpen}>
        <Renderer>
          <CustomTooltip content={tooltipItemMessage}>
            <PopoverTrigger className="relative flex items-center">
              {!isValueValid && <LinkBreak2Icon className="absolute text-red-500 left-1 ml-1"/>}
              <Input
                value={selectedOptionLabel}
                className={cn("justify-between px-2 w-full min-w-[210px]", { 'pl-6': !isValueValid })}
                placeholder="Select target by value..."
                disabled={disabled}
                />
              <Icon type="arrow-bottom" className="absolute right-2"/>
            </PopoverTrigger>
          </CustomTooltip>
        </Renderer>
        <PopoverContent className="w-[200px] p-0">
          <Command
            filter={(value, search) => {
              const relatedOption = options?.find(option => option.value === value);
              if (value === "option-add" && search && canAddOption) return 1
              if (value.toLowerCase().includes(search.toLowerCase())) return 1
              if (relatedOption?.label.toLowerCase().includes(search.toLowerCase())) return 1
              return 0
            }}
          >
            <CommandInput
              ref={searchRef}
              value={search}
              onChangeCapture={searchChangeHandler}
              onKeyDown={(e) => {
                if (e.key === "Enter" && canAddOption) {
                  e.preventDefault();
                  // INFO: if some option should be created later on
                }
              }}
              placeholder="Search..."
              className="h-9"
              disabled={disabled}
            />
            <CommandGroup>
              <div  className="max-h-[300px] overflow-y-auto">
                {(options ?? []).map((option) => {
                  const isValueSelected = value?.value === option.value;
                  return (
                    <CommandItem
                      key={option.value}
                      value={option.value}
                      onSelect={() => {
                        if (!isValueSelected) {
                          onValueChange?.(option);
                        }
                        searchRef.current?.focus();
                      }}
                      className="flex items-left justify-start relative"
                    >
                      <div className="w-6 min-w-6">
                        {isValueSelected && <CheckCircledIcon />}
                      </div>
                      <div className="max-w-full truncate">
                        {option.label}
                      </div>
                    </CommandItem>
                  );
                })}
              </div>
            </CommandGroup>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  )
}
